import type { BaseQueryFn, QueryReturnValue } from "@reduxjs/toolkit/query";

/**
 * Jak dlouho smí čekat jeden pokus dotazu, než ho přerušíme.
 *
 * AIDEV-NOTE: 35 s je nad `timeoutSeconds: 30` Cloud Run `api-prod`: server, který do 30 s neodpoví, vrací 504,
 * takže klient na produkci nikdy neutne dotaz, který by server ještě dokončil. Hosting přitom drží spojení až
 * 60 s, i když Cloud Run už odpověděl, a timeout z toho dělá chybu dřív. Stage má limit Cloud Run 300 s
 * (`gcloud run services describe`), tam může 35 s utnout legitimně pomalý dotaz; to je přijaté. Po timeoutu
 * se dotaz NEOPAKUJE: Cloud Run handler po svém limitu nezastaví a gunicorn gthread `--timeout` vlákna nezabíjí,
 * druhý pokus by zdvojil práci, která na serveru ještě běží (stejný důvod jako u 504). Nad 60 s by timeout
 * nechytil nic. Platí jen pro dotazy. [TD-488]
 */
export const QUERY_TIMEOUT_MS = 35_000;

/**
 * Prodleva před druhým pokusem.
 *
 * AIDEV-NOTE: 502/503 z hostingu přišly za desítky ms, Cloud Run přitom odpověděl včas — chyba je
 * na hraně (Fastly, limit hostingu) a okamžitý pokus by mohl trefit tentýž uzel. 800 ms je dost na
 * přepnutí spojení či uzlu a studentovi to přidá méně než vteřinu. Delší prodleva by jen
 * prodlužovala čekání, o zátěž backendu tu nejde (jediné opakování). [TD-488]
 */
export const QUERY_RETRY_DELAY_MS = 800;

/**
 * Dotazy (GraphQL `query`), které na serveru zapisují, a proto se nesmí opakovat.
 *
 * AIDEV-NOTE: Po timeoutu nebo 5xx první pokus na serveru mohl doběhnout, druhý by zápis udělal znovu.
 * Timeout zůstává (klientský abort server nezastaví, ale student nečeká 60 s), jen bez opakování.
 * Seznam je ručně vedený a vychází z auditu backendu (todas-app, stage): kdo přidá dotaz, který zapisuje
 * (nebo si není jistý), musí jeho název (`endpoint` z `generated.ts`) doplnit sem, jinak se po výpadku
 * zopakuje. Nové pole se zápisem na serveru se musí přidat do `SERVER_WRITING_FIELDS` v
 * `__tests__/nonIdempotentQueries.test.ts`, který seznam proti dokumentům v `queries/` hlídá. Proč který:
 * - `checkAchievements`: ukládá nové `UserAchievement` a mění čítače uživatele, opakování založí duplicity;
 * - `getDashboardData`: obsahuje `checkAchievements(check: true)` a `testPresence(refresh: true)`, při
 *   souběhu obojí zakládá duplicitní dokumenty;
 * - `getPracticeMix`, `getPracticeMhd`: `training`/`trainingMhd` dělají `batch_update` celých
 *   user_assignments, opakování vygeneruje jinou sadu úloh a přepíše tu první;
 * - `getPracticeEnvelope`: `trainingEnvelope` zakládá položku fronty `hierarchy_refresh_queue`. [TD-488]
 */
export const NON_IDEMPOTENT_QUERIES = new Set([
  "checkAchievements",
  "getDashboardData",
  "getPracticeMix",
  "getPracticeMhd",
  "getPracticeEnvelope",
]);

// Selhání přenosu — texty `TypeError` z `fetch` v Chrome, Safari a Firefoxu.
const NETWORK_ERROR_MESSAGE = /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource)/;

// AIDEV-NOTE: Opakuje se jen rychlé selhání: síťová chyba a 502/503, které přicházejí za desítky ms z hrany (Fastly,
// Hosting), server na nich neběží. 504 ani klientský timeout se neopakují: Cloud Run handler po svém limitu
// nezastaví, takže opakování by zdvojilo práci, která na serveru ještě běží. [TD-488]
const RETRYABLE_HTTP_STATUSES = [502, 503];

type Attempt<R> = { result: R } | { thrown: unknown; timedOut: boolean };

const timeoutError = () =>
  Object.assign(new Error(`Request timeout (${QUERY_TIMEOUT_MS} ms)`), { name: "TimeoutError" });

const isRetryable = (attempt: Attempt<QueryReturnValue>) => {
  if ("thrown" in attempt) {
    // Timeout se sem nedostane jako síťová chyba: končí `AbortError` z `controller.abort()`, ne `TypeError`.
    return attempt.thrown instanceof TypeError && NETWORK_ERROR_MESSAGE.test(attempt.thrown.message);
  }

  const { error, meta } = attempt.result;
  const status = (meta as { response?: { status?: number } } | undefined)?.response?.status;
  return error !== undefined && status !== undefined && RETRYABLE_HTTP_STATUSES.includes(status);
};

/**
 * Obalí base query: dotazy dostanou timeout a po rychlém selhání jedno opakování, mutace zůstanou beze změny.
 *
 * AIDEV-NOTE: Vlastní obal místo RTK `retry`. `retry` posílá `retryCondition` jen `error`
 * (`{ name, message, stack }`), HTTP status je až v `meta.response`; pro vyhozené chyby (selhání
 * přenosu, `graphqlRequestBaseQuery` je jen přeposílá) podmínku vůbec nevolá; a přerušení
 * volajícím mění na `CUSTOM_ERROR: "Aborted"`. [TD-488]
 *
 * AIDEV-NOTE: Signál volajícího (`api.signal`, TD-462 přes něj ruší předchozí hledání) a timeout
 * spojuje ručně přes `AbortController`; `AbortSignal.any` je až od Safari 17.4 a `.timeout` od 16
 * a browserslist `>0.2%` zahrnuje starší iOS. Přerušení volajícím se nikdy neopakuje a nehlásí
 * jako timeout. [TD-488]
 */
export const withQueryResilience =
  <Args, Err, Extra extends object, Meta extends object>(
    baseQuery: BaseQueryFn<Args, unknown, Err, Extra, Meta>,
  ): BaseQueryFn<Args, unknown, Err, Extra, Meta> =>
  async (args, api, extraOptions) => {
    if (api.type !== "query") return await baseQuery(args, api, extraOptions);

    const attempt = async (): Promise<Attempt<QueryReturnValue<unknown, Err, Meta>>> => {
      const controller = new AbortController();
      const abortFromCaller = () => {
        controller.abort();
      };
      let timedOut = false;

      if (api.signal.aborted) controller.abort();
      else api.signal.addEventListener("abort", abortFromCaller, { once: true });
      const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, QUERY_TIMEOUT_MS);

      try {
        const result = await baseQuery(args, { ...api, signal: controller.signal }, extraOptions);
        return { result };
      } catch (thrown) {
        return { thrown, timedOut: timedOut && !api.signal.aborted };
      } finally {
        clearTimeout(timer);
        api.signal.removeEventListener("abort", abortFromCaller);
      }
    };

    const settle = (outcome: Attempt<QueryReturnValue<unknown, Err, Meta>>) => {
      if (!("thrown" in outcome)) return outcome.result;
      if (outcome.timedOut) throw timeoutError();

      throw outcome.thrown;
    };

    const first = await attempt();
    if (api.signal.aborted || NON_IDEMPOTENT_QUERIES.has(api.endpoint) || !isRetryable(first)) return settle(first);

    // AIDEV-NOTE: Prodleva se přeruší hned abortem volajícího, ať po něm nezůstane běžet časovač ani druhý pokus.
    // Obal po abortu vrací výsledek prvního pokusu (502, síťová chyba), ne `AbortError`. Volající přesto dostane
    // `AbortError` (`isAbortError` v Subjects.tsx, TD-462) jen proto, že RTK `createAsyncThunk` závodí `payloadCreator`
    // s vlastním `abortedPromise` (`Promise.race`, @reduxjs/toolkit 2.11.2, `src/createAsyncThunk.ts` řádky 647-669)
    // a pozdní výsledek base query zahodí. Obal proto nikdy nepoužívej mimo `createApi` base query: bez toho závodu
    // by volající po abortu dostal chybu prvního pokusu místo přerušení. [TD-488]
    await new Promise<void>(resolve => {
      const done = () => {
        clearTimeout(timer);
        api.signal.removeEventListener("abort", done);
        resolve();
      };
      const timer = setTimeout(done, QUERY_RETRY_DELAY_MS);
      api.signal.addEventListener("abort", done, { once: true });
    });

    return api.signal.aborted ? settle(first) : settle(await attempt());
  };
